'use client'

import {
  CaretDownOutlined,
  CheckCircleOutlined,
  HomeOutlined,
  KeyOutlined,
  LogoutOutlined,
  MenuOutlined,
} from '@ant-design/icons'

import { useCallback, useEffect, useMemo, useState } from 'react'
import { Avatar, Dropdown, Popover, Typography, message } from 'antd'
import * as R from 'ramda'
import type { ItemType } from 'antd/lib/menu/hooks/useItems'
import type { IProject, IUserInfo } from '../../hooks/user'
import type { ProjectScopeType } from '../../utils'
import { logout } from '../../utils'
import { useSiteContext } from '@/context/site-context'
import cn from 'classnames'
import s from '@/app/components/header/index.module.css'

import './topbar.css'
import {
  NotificationClientOptions,
  useNotification,
} from '@/app/components/notification/hooks'
import Announcement from '@/app/components/base/announcement'
import { useCustomWebsiteLogo } from './hooks'
import { useContext } from 'use-context-selector'
import AppContext from '@/context/app-context'

const scopeText: Record<ProjectScopeType, string> = {
  domain: '域管理后台',
  system: '管理后台',
  project: '普通项目',
}

type IProjectSelectProps = {
  currentProject?: IProject | null
  projectList?: IProject[] | null
  systemProjectList?: IProject[] | null
  domainProjectList?: IProject[] | null
  projectScope?: ProjectScopeType

  onProjectChange?: (
    project: IProject,
    scope: ProjectScopeType,
  ) => void | Promise<void>
}

function ProjectSelect(props: IProjectSelectProps) {
  const {
    currentProject,
    projectList,
    systemProjectList,
    domainProjectList,
    projectScope,
    onProjectChange,
  } = props

  const [selectOpen, setSelectOpen] = useState(false)
  const handleProjectSelected = (
    project: IProject,
    scope: ProjectScopeType,
  ) => {
    onProjectChange?.(project, scope)
  }
  const subList = (
    <>
      {(systemProjectList?.length || 0) > 0 && (
        <>
          <div>管理后台</div>
          {systemProjectList?.map(p => (
            <div
              key={p.id}
              className='flex justify-between h-full truncate items-center'
              onClick={() => {
                handleProjectSelected(p, 'system')
                setSelectOpen(false)
              }}>
              <div className='flex-auto'>{p.name}</div>
              {currentProject?.id === p.id && projectScope === 'system' ? (
                <div className='ml-1'>
                  <CheckCircleOutlined
                    style={{ marginLeft: '10px', color: 'green' }}
                  />
                </div>
              ) : null}
            </div>
          ))}
        </>
      )}

      {(domainProjectList?.length || 0) > 0 && (
        <>
          <div>域管理后台</div>
          {domainProjectList?.map(p => (
            <div
              key={p.id}
              className='flex justify-between h-full truncate items-center'
              onClick={() => {
                handleProjectSelected(p, 'domain')
                setSelectOpen(false)
              }}>
              <div className='flex-auto'>{p.name}</div>
              {currentProject?.id === p.id && projectScope === 'domain' ? (
                <div className='ml-1'>
                  <CheckCircleOutlined
                    style={{ marginLeft: '10px', color: 'green' }}
                  />
                </div>
              ) : null}
            </div>
          ))}
        </>
      )}

      <div>普通项目</div>
      {projectList?.map(p => (
        <div
          key={p.id}
          className='flex justify-between h-full truncate items-center'
          onClick={() => {
            handleProjectSelected(p, 'project')
            setSelectOpen(false)
          }}>
          <div className='flex-auto'>{p.name}</div>
          {currentProject?.id === p.id && projectScope === 'project' ? (
            <div className='ml-1'>
              <CheckCircleOutlined
                style={{ marginLeft: '10px', color: 'green' }}
              />
            </div>
          ) : null}
        </div>
      ))}
    </>
  )
  return (
    <Popover
      trigger='click'
      content={subList}
      open={selectOpen}
      onOpenChange={open => setSelectOpen(open)}>
      <button className='project-select-button bg-gray-100 flex items-center justify-center'>
        <HomeOutlined />
        <span className='current-project-label ml-2 text-truncate'>
          {(!!projectScope && scopeText[projectScope]) || '-'}
        </span>
        <div className='drop-icon'>
          <CaretDownOutlined />
        </div>
      </button>
    </Popover>
  )
}

type IUserInfoProps = {
  user?: IUserInfo | null
  isSuper?: boolean
}

function UserInfo({ user, isSuper }: IUserInfoProps) {
  const iconName = user?.name.split('')[0].toUpperCase() ?? '-'
  const items: ItemType[] = [
    // {
    //   title: "多云统一登录",
    //   key: "cloudLogin",
    //   label: "多云统一登录",
    //   icon: <CloudOutlined className="ml-2 mr-2" />,
    // },
    // {
    //   title: '更改密码',
    //   key: 'updatePassword',
    //   label: '更改密码',
    //   icon: <UserDeleteOutlined className='ml-2 mr-2' />,
    // },
    isSuper ? {
      title: 'License设置',
      key: 'licenseSetup',
      label: 'License设置',
      icon: <KeyOutlined />,
    } : null,
    {
      title: '退出',
      key: 'logout',
      label: '退出',
      icon: <LogoutOutlined className='mr-2' />,
    },
  ]
  const handleMenuClicked = async ({ key }: { key: string }) => {
    if (key === 'logout') {
      try {
        await logout()
        // 清除体验中心的 localStorage 数据
        localStorage.removeItem('maas_trial_messages')
        localStorage.removeItem('maas_trial_system_prompt')
        globalThis.location.replace('/iaas/auth/login')
      } catch (error) {
        console.error(error)
        throw error
      }
    } else if (key === 'handleUpdatePassword') {
      // TODO: create dialog
      // this.createDialog("UpdateUserPasswordDialog");
    } else if (key === 'cloudLogin') {
      globalThis.location.replace('/clouduser')
    } else if (key === 'licenseSetup') {
      globalThis.location.replace('/llm/setup/license')
    }
  }
  return (
    <Dropdown
      trigger={['click']}
      menu={{ items, onClick: handleMenuClicked }}
      placement='bottomRight'
      className='cursor-pointer '>
      <div className='flex items-center justify-between space-x-2 pl-5 pr-5'>
        <Avatar className='nav-avatar bg-menuBlue'>
          <span>{iconName}</span>
        </Avatar>
        <Typography.Text ellipsis className='nav-username'>
          {user?.name ?? '--'}
        </Typography.Text>
      </div>
    </Dropdown>
  )
}

export type ITopbarProps = {
  children?: React.ReactNode
  title?: string
  currentProject?: IProject | null
  userInfo?: IUserInfo | null
  projectList?: IProject[] | null
  projectScope?: ProjectScopeType

  // fetchProjects?: () => Promise<IProject[] | null>;
  onMenuClicked?: () => void
  onProjectChange?: (
    project: IProject,
    scope: ProjectScopeType,
  ) => void | Promise<void>
}

function isSingleProject(projects: IProject[], item: IProject) {
  const sameDomainProjects = projects.filter(
    v => v.domain_id === item.domain_id,
  )
  const num = sameDomainProjects?.length
  if (num > 1) {
    const isAll = sameDomainProjects.every(v => {
      return R.equals(v.domain_policies, item.domain_policies)
    })

    if (isAll) return true
  }
  return num === 1
}

export default function Topbar(props: ITopbarProps) {
  const {
    children,
    title,
    currentProject,
    userInfo,
    projectList,
    projectScope,
    onMenuClicked,
    onProjectChange,
  } = props
  const { userProfile } = useContext(AppContext)
  const [projectSelectList, setProjectSelectList] = useState<IProject[]>([])
  const [systemProjectList, setSystemProjectList] = useState<IProject[]>([])
  const [domainProjectList, setDomainProjectList] = useState<IProject[]>([])
  const site = useSiteContext()

  const handleNotificationMessage = useCallback(
    (msg: string, type: 'success' | 'warning') => {
      message[type](msg)
    },
    [],
  )

  const notificationOptions: NotificationClientOptions = useMemo(
    () => ({
      server: '/',
      onMessage: handleNotificationMessage,
    }),
    [handleNotificationMessage],
  )

  useNotification(notificationOptions)
  const customLogo = useCustomWebsiteLogo();

  useEffect(() => {
    const pList = R.sort((a, b) => {
      return a.name.localeCompare(b.name)
    }, projectList ?? [])
    setProjectSelectList(pList)
    setSystemProjectList(pList.filter(v => v.system_capable))
    setDomainProjectList(() => {
      const ret = pList.filter(v => v.domain_capable === true)
      return R.uniqWith((a: IProject, b: IProject) => {
        return (
          isSingleProject(ret, a) &&
          isSingleProject(ret, b) &&
          a.domain_id === b.domain_id &&
          R.equals(a.domain_policies, b.domain_policies)
        )
      })(ret)
    })
  }, [projectList])

  return (
    <div className='topbar flex items-center justify-between shadow-md bg-white'>
      <div
        className='menu-icon flex items-center justify-center flex-shrink-0 flex-grow-0 bg-menuBlue text-white'
        onClick={onMenuClicked}>
        <MenuOutlined />
      </div>
      <div className='flex items-center h-100'>
        <div className='header-logo mx-4'>
          {!customLogo && <div className={cn(s[site.logo_cn])}></div>}
          {customLogo && <img className="logo-img" src={customLogo}></img>}
        </div>
        <h1 className='header-title mb-0'>{title}</h1>
        <ProjectSelect
          projectList={projectSelectList}
          systemProjectList={systemProjectList}
          domainProjectList={domainProjectList}
          currentProject={currentProject}
          projectScope={projectScope}
          onProjectChange={onProjectChange}
        />
      </div>
      {/* 公告栏 */}
      <div className='flex items-center mx-3 w-1/2'>
        <Announcement />
      </div>
      <div className='flex ml-auto'>
        <div className='flex space-x-4'>
          {children}
          <UserInfo user={userInfo} isSuper={userProfile?.is_super} />
        </div>
      </div>
    </div>
  )
}
